import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Tabs,
  Table,
  Statistic,
  Descriptions,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  message,
  Popconfirm,
  Breadcrumb,
  Empty,
  Progress,
  DatePicker,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  ThunderboltOutlined,
  MailOutlined,
  BarChartOutlined,
  CommentOutlined,
  PlusOutlined,
  SyncOutlined,
  EyeOutlined,
  SendOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type {
  Campaign,
  CampaignStats,
  InfluencerInsight,
  OutreachMessage,
  SendStats,
  SendTask,
  SendProgress,
  Reply,
  CreateInsightRequest,
  GenerateMessageRequest,
  UpdateMessageRequest,
  ChannelType,
} from '../../types/outreach';
import { CHANNEL_LABELS, CHANNEL_COLORS, CHANNEL_CONTENT_HINTS } from '../../types/outreach';
import { outreachApi } from '../../services/outreach';
import { ROUTES } from '../../config/routes';
import { POLL_CONFIG, showPollTimeoutNotification } from '../../utils/polling';
import {
  CAMPAIGN_STATUS_MAP,
  MESSAGE_STATUS_MAP,
  SEND_TASK_STATUS_MAP,
  INTENT_MAP,
  INSIGHT_STATUS_MAP,
} from '../../constants/statusMappings';
import { renderStatusTag } from '../../utils/statusHelpers';
import { COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<SendStats | null>(null);
  const [insights, setInsights] = useState<InfluencerInsight[]>([]);
  const [messages, setMessages] = useState<OutreachMessage[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [activeTab, setActiveTab] = useState('basic');

  // 发送任务相关状态
  const [sendProgress, setSendProgress] = useState<SendProgress | null>(null);
  const [sendTasks, setSendTasks] = useState<SendTask[]>([]);
  const [sendTasksTotal, setSendTasksTotal] = useState(0);
  const [sendTasksLoading, setSendTasksLoading] = useState(false);
  const [sendTasksStatus, setSendTasksStatus] = useState<string>('');
  const [scheduledSendTime, setScheduledSendTime] = useState<dayjs.Dayjs | null>(null);
  const [batchSendModalVisible, setBatchSendModalVisible] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [insightModalVisible, setInsightModalVisible] = useState(false);
  const [insightDetailModalVisible, setInsightDetailModalVisible] = useState(false);
  const [generateMsgModalVisible, setGenerateMsgModalVisible] = useState(false);
  const [editMsgModalVisible, setEditMsgModalVisible] = useState(false);
  const [replyDetailModalVisible, setReplyDetailModalVisible] = useState(false);

  // AI processing states
  const [aiProcessing, setAiProcessing] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<InfluencerInsight | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<OutreachMessage | null>(null);
  const [selectedReply, setSelectedReply] = useState<Reply | null>(null);

  const [editForm] = Form.useForm();
  const [insightForm] = Form.useForm();
  const [generateMsgForm] = Form.useForm();
  const [editMsgForm] = Form.useForm();

  const fetchCampaignDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await outreachApi.getCampaign(id);
      if (result.data) {
        setCampaign(result.data);
      }
    } catch (error) {
      console.error('获取活动详情失败:', error);
      message.error('获取活动详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchStats = useCallback(async () => {
    if (!id) return;
    try {
      const result = await outreachApi.getSendStats(id);
      if (result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  }, [id]);

  const fetchInsights = useCallback(async () => {
    // Note: 后端暂无获取活动下洞察列表的API，这里模拟
    setInsights([]);
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!id) return;
    try {
      const result = await outreachApi.getMessages(id);
      if (result.data) {
        setMessages(result.data.list || []);
      }
    } catch (error) {
      console.error('获取消息列表失败:', error);
    }
  }, [id]);

  const fetchReplies = useCallback(async () => {
    try {
      const result = await outreachApi.getReplies();
      if (result.data) {
        // 过滤当前活动的回复
        const campaignReplies = (result.data.list || []).filter(
          (r: Reply) => r.message_id?.includes(id || '')
        );
        setReplies(campaignReplies);
      }
    } catch (error) {
      console.error('获取回复列表失败:', error);
    }
  }, [id]);

  // 获取发送进度
  const fetchSendProgress = useCallback(async () => {
    if (!id) return;
    try {
      const result = await outreachApi.getSendProgress(id);
      if (result.data) {
        setSendProgress(result.data);
      }
    } catch (error) {
      console.error('获取发送进度失败:', error);
    }
  }, [id]);

  // 获取发送任务列表
  const fetchSendTasks = useCallback(async (page = 1, status = '') => {
    if (!id) return;
    setSendTasksLoading(true);
    try {
      const result = await outreachApi.listSendTasks(id, {
        page,
        page_size: 20,
        status: status || undefined,
      });
      if (result.data) {
        setSendTasks(result.data.list || []);
        setSendTasksTotal(result.data.meta?.total || 0);
      }
    } catch (error) {
      console.error('获取发送任务列表失败:', error);
    } finally {
      setSendTasksLoading(false);
    }
  }, [id]);

  // 重试发送任务
  const handleRetrySendTask = async (taskId: string) => {
    try {
      await outreachApi.retrySendTask(taskId);
      message.success('重试任务已提交');
      fetchSendTasks();
      fetchSendProgress();
    } catch (error) {
      console.error('重试任务失败:', error);
      message.error('重试任务失败');
    }
  };

  // 批量发送
  const handleBatchSend = async () => {
    if (!id) return;
    try {
      const data: { recipient_ids: string[]; scheduled_at?: string } = {
        recipient_ids: selectedRecipientIds,
      };
      if (scheduledSendTime) {
        data.scheduled_at = scheduledSendTime.toISOString();
      }
      const result = await outreachApi.batchSend(id, data);
      if (result.data) {
        message.success(result.data.message);
        setBatchSendModalVisible(false);
        setSelectedRecipientIds([]);
        setScheduledSendTime(null);
        fetchSendProgress();
        fetchSendTasks();
      }
    } catch (error) {
      console.error('批量发送失败:', error);
      message.error('批量发送失败');
    }
  };

  useEffect(() => {
    fetchCampaignDetail();
    fetchStats();
    fetchInsights();
    fetchMessages();
    fetchReplies();
    fetchSendProgress();
    fetchSendTasks();
  }, [id, fetchCampaignDetail, fetchStats, fetchInsights, fetchMessages, fetchReplies, fetchSendProgress, fetchSendTasks]);

  // 轮询发送进度（发送中时每 3 秒刷新）
  useEffect(() => {
    if (!sendProgress || (sendProgress.sending === 0 && sendProgress.queued === 0 && sendProgress.scheduled === 0)) {
      return;
    }
    const interval = setInterval(() => {
      fetchSendProgress();
      fetchSendTasks();
    }, 3000);
    return () => clearInterval(interval);
  }, [sendProgress, fetchSendProgress, fetchSendTasks]);

  // 获取状态Tag
  const getStatusTag = (status: string) => renderStatusTag(status, CAMPAIGN_STATUS_MAP);

  // 获取消息状态Tag
  const getMsgStatusTag = (status: string) => renderStatusTag(status, MESSAGE_STATUS_MAP);

  // 获取发送任务状态Tag
  const getSendTaskStatusTag = (status: string) => renderStatusTag(status, SEND_TASK_STATUS_MAP);

  // 获取意向Tag（4种枚举: positive / neutral / negative / follow_up）
  const getIntentTag = (intent?: string) => {
    if (!intent) return '-';
    return renderStatusTag(intent, INTENT_MAP);
  };

  // 更新活动
  const handleUpdateCampaign = async (values: Partial<Campaign>) => {
    if (!id) return;
    try {
      await outreachApi.updateCampaign(id, values);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchCampaignDetail();
    } catch (error) {
      console.error('更新活动失败:', error);
      message.error('更新活动失败');
    }
  };

  // 删除活动
  const handleDeleteCampaign = async () => {
    if (!id) return;
    try {
      await outreachApi.deleteCampaign(id);
      message.success('删除成功');
      navigate(`${ROUTES.OUTREACH}/campaigns`);
    } catch (error) {
      console.error('删除活动失败:', error);
      message.error('删除活动失败');
    }
  };

  // 创建洞察
  const handleCreateInsight = async (values: CreateInsightRequest) => {
    setAiProcessing(true);
    try {
      const result = await outreachApi.createInsight({
        ...values,
        influencer_id: values.influencer_id || `temp_${Date.now()}`,
      });
      if (result.data) {
        message.success('洞察任务已创建，AI正在分析中...');
        setInsightModalVisible(false);
        insightForm.resetFields();
        // 轮询检查状态
        pollInsightStatus(result.data.id);
      }
    } catch (error) {
      console.error('创建洞察失败:', error);
      message.error('创建洞察失败');
      setAiProcessing(false);
    }
  };

  // 轮询洞察状态（使用 POLL_CONFIG 统一轮询参数）
  const pollInsightStatus = async (insightId: string) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const result = await outreachApi.getInsight(insightId);
        if (result.data?.status === 'completed' || result.data?.status === 'failed') {
          clearInterval(interval);
          setAiProcessing(false);
          if (result.data.status === 'completed') {
            message.success('洞察分析完成');
            fetchInsights();
          } else {
            message.error('洞察分析失败');
          }
          return;
        }
      } catch (error) {
        clearInterval(interval);
        setAiProcessing(false);
        message.error('获取洞察状态失败');
        return;
      }
      if (attempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        clearInterval(interval);
        setAiProcessing(false);
        showPollTimeoutNotification(`outreach-insight-${insightId}`, () => pollInsightStatus(insightId));
      }
    }, POLL_CONFIG.INTERVAL);
  };

  // 生成消息
  const handleGenerateMessages = async (values: GenerateMessageRequest) => {
    if (!id) return;
    setAiProcessing(true);
    try {
      const result = await outreachApi.generateMessages(id, values);
      if (result.data) {
        message.success('消息生成任务已创建，AI正在生成中...');
        setGenerateMsgModalVisible(false);
        generateMsgForm.resetFields();
        // 轮询检查消息状态
        pollMessagesStatus();
      }
    } catch (error) {
      console.error('生成消息失败:', error);
      message.error('生成消息失败');
      setAiProcessing(false);
    }
  };

  // 轮询消息状态（使用 POLL_CONFIG 统一轮询参数）
  const pollMessagesStatus = async () => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const result = await outreachApi.getMessages(id!);
        const msgs = result.data?.list || [];
        const allDone = msgs.every(
          (m: OutreachMessage) => m.status !== 'generating'
        );
        if (allDone) {
          clearInterval(interval);
          setAiProcessing(false);
          fetchMessages();
          message.success('消息生成完成');
          return;
        }
        if (attempts >= POLL_CONFIG.MAX_ATTEMPTS) {
          clearInterval(interval);
          setAiProcessing(false);
          fetchMessages();
          showPollTimeoutNotification('outreach-generate-messages', () => {
            setAiProcessing(true);
            pollMessagesStatus();
          });
        }
      } catch (error) {
        clearInterval(interval);
        setAiProcessing(false);
      }
    }, POLL_CONFIG.INTERVAL);
  };

  // 更新消息
  const handleUpdateMessage = async (values: UpdateMessageRequest) => {
    if (!id || !selectedMessage) return;
    try {
      await outreachApi.updateMessage(id, selectedMessage.id, values);
      message.success('消息更新成功');
      setEditMsgModalVisible(false);
      fetchMessages();
    } catch (error) {
      console.error('更新消息失败:', error);
      message.error('更新消息失败');
    }
  };

  // 批量发送
  const handleSendMessages = async () => {
    if (!id) return;
    try {
      await outreachApi.sendMessages(id);
      message.success('消息发送任务已启动');
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('发送消息失败:', error);
      message.error('发送消息失败');
    }
  };

  // 分析回复
  const handleAnalyzeReply = async (replyId: string) => {
    setAiProcessing(true);
    try {
      await outreachApi.analyzeReply(replyId);
      message.success('回复分析任务已启动，请稍后刷新查看');
    } catch (error) {
      console.error('分析回复失败:', error);
      message.error('分析回复失败');
    } finally {
      setAiProcessing(false);
    }
  };

  // 生成跟进
  const handleGenerateFollowUp = async (replyId: string) => {
    setAiProcessing(true);
    try {
      await outreachApi.generateFollowUp(replyId);
      message.success('跟进内容生成任务已启动，请稍后刷新查看');
    } catch (error) {
      console.error('生成跟进失败:', error);
      message.error('生成跟进失败');
    } finally {
      setAiProcessing(false);
    }
  };

  // Tab 1: 基本信息内容
  const BasicInfoTab = () => (
    <div>
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="目标红人" value={campaign?.target_count || 0} suffix="人" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已发送" value={campaign?.sent_count || 0} suffix="封" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="回复数" value={campaign?.reply_count || 0} suffix="封" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="成功数" value={campaign?.success_count || 0} suffix="个" />
          </Card>
        </Col>
      </Row>

      <Card title="活动信息">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="活动名称">{campaign?.name}</Descriptions.Item>
          <Descriptions.Item label="状态">{getStatusTag(campaign?.status || 'draft')}</Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {campaign?.created_at ? new Date(campaign.created_at).toLocaleString('zh-CN') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {campaign?.updated_at ? new Date(campaign.updated_at).toLocaleString('zh-CN') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            {campaign?.notes || '-'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 16 }}>
          <Button type="primary" icon={<EditOutlined />} onClick={() => setEditModalVisible(true)}>
            编辑活动
          </Button>
        </div>
      </Card>
    </div>
  );

  // Tab 2: 红人洞察
  const InsightTab = () => {
    const columns = [
      {
        title: '红人URL',
        dataIndex: 'influencer_url',
        key: 'influencer_url',
        ellipsis: true,
        render: (url: string) => (
          <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => renderStatusTag(status, INSIGHT_STATUS_MAP),
      },
      {
        title: '创建时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
      },
      {
        title: '操作',
        key: 'action',
        width: 120,
        render: (_: unknown, record: InfluencerInsight) => (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedInsight(record);
              setInsightDetailModalVisible(true);
            }}
          >
            查看报告
          </Button>
        ),
      },
    ];

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setInsightModalVisible(true)}
            loading={aiProcessing}
          >
            发起洞察
          </Button>
          {aiProcessing && (
            <span style={{ marginLeft: 16, color: COLORS.primary }}>
              <SyncOutlined spin /> AI处理中...
            </span>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={insights}
          rowKey="id"
          pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          locale={{ emptyText: <Empty description="暂无洞察报告" /> }}
        />
      </div>
    );
  };

  // Tab 3: 邀约消息
  const MessagesTab = () => {
    const columns = [
      {
        title: '红人ID',
        dataIndex: 'influencer_id',
        key: 'influencer_id',
        ellipsis: true,
      },
      {
        title: '渠道',
        dataIndex: 'channel',
        key: 'channel',
        width: 100,
        render: (channel: ChannelType) => (
          <Tag color={CHANNEL_COLORS[channel] || 'default'}>
            {CHANNEL_LABELS[channel] || channel}
          </Tag>
        ),
      },
      {
        title: '主题',
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
        render: (subject: string) => subject || '-',
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => getMsgStatusTag(status),
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        render: (_: unknown, record: OutreachMessage) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedMessage(record);
                editMsgForm.setFieldsValue({
                  subject: record.subject,
                  content: record.content,
                });
                setEditMsgModalVisible(true);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedMessage(record);
                setEditMsgModalVisible(true);
              }}
            >
              预览
            </Button>
          </Space>
        ),
      },
    ];

    const readyCount = messages.filter((m) => m.status === 'ready').length;

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => setGenerateMsgModalVisible(true)}
            loading={aiProcessing}
          >
            批量生成
          </Button>
          <Popconfirm
            title="确认发送"
            description={`确定发送 ${readyCount} 条就绪消息吗？`}
            onConfirm={handleSendMessages}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="default"
              icon={<SendOutlined />}
              disabled={readyCount === 0}
            >
              发送 ({readyCount})
            </Button>
          </Popconfirm>
          {aiProcessing && (
            <span style={{ color: COLORS.primary }}>
              <SyncOutlined spin /> AI处理中...
            </span>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={messages}
          rowKey="id"
          pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          locale={{ emptyText: <Empty description="暂无邀约消息" /> }}
        />
      </div>
    );
  };

  // Tab 4: 发送统计
  const StatsTab = () => {
    const summary = stats?.summary || {
      total: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      replied: 0,
      failed: 0,
    };

    return (
      <div>
        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="待发送"
                value={(summary.total || 0) - (summary.sent || 0)}
                valueStyle={{ color: COLORS.textSecondary }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="发送中"
                value={stats?.by_status?.sending || 0}
                valueStyle={{ color: COLORS.primary }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已送达"
                value={summary.delivered || 0}
                valueStyle={{ color: COLORS.success }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已读"
                value={summary.read || 0}
                valueStyle={{ color: COLORS.purple }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="失败"
                value={summary.failed || 0}
                valueStyle={{ color: COLORS.error }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="回复分析">
          <Row gutter={24}>
            <Col span={6}>
              <Statistic
                title="积极回复"
                value={stats?.reply_analysis?.positive || 0}
                valueStyle={{ color: COLORS.success }}
                suffix="封"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="中性"
                value={stats?.reply_analysis?.neutral || 0}
                valueStyle={{ color: COLORS.textSecondary }}
                suffix="封"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="消极"
                value={stats?.reply_analysis?.negative || 0}
                valueStyle={{ color: COLORS.error }}
                suffix="封"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="待跟进"
                value={stats?.reply_analysis?.follow_up || 0}
                valueStyle={{ color: COLORS.orange }}
                suffix="封"
              />
            </Col>
          </Row>
        </Card>
      </div>
    );
  };

  // Tab 5: 发送任务
  const SendTasksTab = () => {
    const columns = [
      {
        title: '收件人',
        dataIndex: 'recipient_address',
        key: 'recipient_address',
        ellipsis: true,
        render: (addr: string, record: SendTask) => (
          <span>{record.influencer_name || addr}</span>
        ),
      },
      {
        title: '渠道',
        dataIndex: 'channel',
        key: 'channel',
        width: 80,
        render: (channel: ChannelType) => (
          <Tag color={CHANNEL_COLORS[channel] || 'default'}>
            {CHANNEL_LABELS[channel] || channel}
          </Tag>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => getSendTaskStatusTag(status),
      },
      {
        title: '发送时间',
        dataIndex: 'sent_at',
        key: 'sent_at',
        width: 160,
        render: (time: string, record: SendTask) => {
          if (record.status === 'scheduled' && record.scheduled_at) {
            return (
              <span style={{ color: COLORS.primary }}>
                <ClockCircleOutlined /> {new Date(record.scheduled_at).toLocaleString('zh-CN')}
              </span>
            );
          }
          return time ? new Date(time).toLocaleString('zh-CN') : '-';
        },
      },
      {
        title: '重试次数',
        dataIndex: 'retry_count',
        key: 'retry_count',
        width: 80,
        render: (count: number) => (count > 0 ? <Tag color="orange">{count}</Tag> : '0'),
      },
      {
        title: '错误信息',
        dataIndex: 'error_message',
        key: 'error_message',
        ellipsis: true,
        render: (msg: string) => msg || '-',
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        render: (_: unknown, record: SendTask) => (
          record.status === 'failed' ? (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRetrySendTask(record.id)}
            >
              重试
            </Button>
          ) : null
        ),
      },
    ];

    // 计算进度百分比
    const progressPercent = sendProgress && sendProgress.total > 0
      ? Math.round(((sendProgress.sent + sendProgress.failed) / sendProgress.total) * 100)
      : 0;

    return (
      <div>
        {/* 发送进度 */}
        <Card title="发送进度" style={{ marginBottom: 16 }}>
          {sendProgress && (
            <>
              <Progress
                percent={progressPercent}
                status={sendProgress.sending > 0 ? 'active' : undefined}
                style={{ marginBottom: 16 }}
              />
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic title="总数" value={sendProgress.total} />
                </Col>
                <Col span={4}>
                  <Statistic title="排队中" value={sendProgress.queued} valueStyle={{ color: COLORS.cyan }} />
                </Col>
                <Col span={4}>
                  <Statistic title="已定时" value={sendProgress.scheduled} valueStyle={{ color: COLORS.primary }} />
                </Col>
                <Col span={4}>
                  <Statistic title="发送中" value={sendProgress.sending} valueStyle={{ color: COLORS.primary }} />
                </Col>
                <Col span={4}>
                  <Statistic title="已发送" value={sendProgress.sent} valueStyle={{ color: COLORS.success }} />
                </Col>
                <Col span={4}>
                  <Statistic title="失败" value={sendProgress.failed} valueStyle={{ color: COLORS.error }} />
                </Col>
              </Row>
            </>
          )}
        </Card>

        {/* 操作区 */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setBatchSendModalVisible(true)}
          >
            批量发送
          </Button>
          <Select
            placeholder="筛选状态"
            style={{ width: 150 }}
            allowClear
            value={sendTasksStatus || undefined}
            onChange={(value) => {
              setSendTasksStatus(value || '');
              fetchSendTasks(1, value || '');
            }}
          >
            <Option value="queued">排队中</Option>
            <Option value="scheduled">已定时</Option>
            <Option value="sending">发送中</Option>
            <Option value="sent">已发送</Option>
            <Option value="failed">失败</Option>
          </Select>
          <Button icon={<SyncOutlined />} onClick={() => { fetchSendProgress(); fetchSendTasks(); }}>
            刷新
          </Button>
        </div>

        {/* 任务列表 */}
        <Table
          columns={columns}
          dataSource={sendTasks}
          rowKey="id"
          loading={sendTasksLoading}
          pagination={{
            total: sendTasksTotal,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page) => fetchSendTasks(page, sendTasksStatus),
          }}
          locale={{ emptyText: <Empty description="暂无发送任务" /> }}
        />
      </div>
    );
  };

  // Tab 6: 回复监测
  const RepliesTab = () => {
    const columns = [
      {
        title: '红人ID',
        dataIndex: 'influencer_id',
        key: 'influencer_id',
        ellipsis: true,
      },
      {
        title: '意向',
        dataIndex: 'intent_classification',
        key: 'intent_classification',
        width: 100,
        render: (intent: string) => getIntentTag(intent),
      },
      {
        title: '回复时间',
        dataIndex: 'received_at',
        key: 'received_at',
        width: 180,
        render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
      },
      {
        title: '操作',
        key: 'action',
        width: 250,
        render: (_: unknown, record: Reply) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedReply(record);
                setReplyDetailModalVisible(true);
              }}
            >
              详情
            </Button>
            <Button
              type="link"
              size="small"
              icon={<RobotOutlined />}
              onClick={() => handleAnalyzeReply(record.id)}
              loading={aiProcessing}
            >
              分析回复
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CommentOutlined />}
              onClick={() => handleGenerateFollowUp(record.id)}
              loading={aiProcessing}
            >
              生成跟进
            </Button>
          </Space>
        ),
      },
    ];

    return (
      <div>
        {aiProcessing && (
          <div style={{ marginBottom: 16, color: COLORS.primary }}>
            <SyncOutlined spin /> AI处理中...
          </div>
        )}

        <Table
          columns={columns}
          dataSource={replies}
          rowKey="id"
          pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          locale={{ emptyText: <Empty description="暂无回复记录" /> }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* 面包屑 */}
      <Breadcrumb items={[
        { title: <Link to={`${ROUTES.OUTREACH}/campaigns`}>邀约活动</Link> },
        { title: campaign?.name || '活动详情' },
      ]} style={{ marginBottom: 16 }} />

      {/* 头部 */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>{campaign?.name || '活动详情'}</Title>
            {getStatusTag(campaign?.status || 'draft')}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setEditModalVisible(true)}>
              编辑
            </Button>
            <Popconfirm
              title="确认删除"
              description="确定要删除此活动吗？此操作不可恢复。"
              onConfirm={handleDeleteCampaign}
              okText="确认"
              cancelText="取消"
            >
              <Button danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      {/* Tab内容 */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'basic',
            label: (
              <span>
                <InboxOutlined />
                基本信息
              </span>
            ),
            children: <BasicInfoTab />,
          },
          {
            key: 'insights',
            label: (
              <span>
                <ThunderboltOutlined />
                红人洞察
              </span>
            ),
            children: <InsightTab />,
          },
          {
            key: 'messages',
            label: (
              <span>
                <MailOutlined />
                邀约消息
              </span>
            ),
            children: <MessagesTab />,
          },
          {
            key: 'stats',
            label: (
              <span>
                <BarChartOutlined />
                发送统计
              </span>
            ),
            children: <StatsTab />,
          },
          {
            key: 'send-tasks',
            label: (
              <span>
                <SendOutlined />
                发送任务
              </span>
            ),
            children: <SendTasksTab />,
          },
          {
            key: 'replies',
            label: (
              <span>
                <CommentOutlined />
                回复监测
              </span>
            ),
            children: <RepliesTab />,
          },
        ]}
      />

      {/* 编辑活动Modal */}
      <Modal
        title="编辑活动"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateCampaign}
          initialValues={{
            name: campaign?.name,
            notes: campaign?.notes,
          }}
        >
          <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="请输入活动名称" maxLength={100} />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={4} placeholder="请输入备注信息" maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发起洞察Modal */}
      <Modal
        title="发起红人洞察"
        open={insightModalVisible}
        onCancel={() => {
          setInsightModalVisible(false);
          insightForm.resetFields();
        }}
        onOk={() => insightForm.submit()}
        confirmLoading={aiProcessing}
        okText="开始分析"
        cancelText="取消"
      >
        <Form form={insightForm} layout="vertical" onFinish={handleCreateInsight}>
          <Form.Item name="influencer_url" label="红人主页URL" rules={[{ required: true, message: '请输入红人主页URL' }]}>
            <Input placeholder="例如：https://www.tiktok.com/@username" />
          </Form.Item>
          <Form.Item name="product_category" label="产品类别">
            <Input placeholder="例如：美妆、数码、服饰" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 洞察报告详情Modal */}
      <Modal
        title="洞察报告详情"
        open={insightDetailModalVisible}
        onCancel={() => {
          setInsightDetailModalVisible(false);
          setSelectedInsight(null);
        }}
        footer={null}
        width={800}
      >
        {selectedInsight && (
          <div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="红人URL">{selectedInsight.influencer_url}</Descriptions.Item>
              <Descriptions.Item label="状态">{selectedInsight.status}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(selectedInsight.created_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
            </Descriptions>
            
            {selectedInsight.status === 'completed' && selectedInsight.report_content && (
              <Card title="AI分析结果" style={{ marginTop: 16 }} size="small">
                {typeof selectedInsight.report_content === 'string' ? (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedInsight.report_content}
                  </Paragraph>
                ) : (
                  <Descriptions bordered column={1} size="small">
                    {selectedInsight.report_content.basic_info && (
                      <Descriptions.Item label="基本信息">
                        {JSON.stringify(selectedInsight.report_content.basic_info, null, 2)}
                      </Descriptions.Item>
                    )}
                    {selectedInsight.report_content.content_style && (
                      <Descriptions.Item label="内容风格">
                        {selectedInsight.report_content.content_style}
                      </Descriptions.Item>
                    )}
                    {selectedInsight.report_content.collaboration_preference && (
                      <Descriptions.Item label="合作偏好">
                        {JSON.stringify(selectedInsight.report_content.collaboration_preference, null, 2)}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* 批量生成消息Modal */}
      <Modal
        title="批量生成邀约消息"
        open={generateMsgModalVisible}
        onCancel={() => {
          setGenerateMsgModalVisible(false);
          generateMsgForm.resetFields();
        }}
        onOk={() => generateMsgForm.submit()}
        confirmLoading={aiProcessing}
        okText="开始生成"
        cancelText="取消"
        width={600}
      >
        <Form form={generateMsgForm} layout="vertical" onFinish={handleGenerateMessages}>
          <Form.Item name="influencer_ids" label="红人ID列表" rules={[{ required: true, message: '请输入红人ID' }]}>
            <Select
              mode="tags"
              placeholder="输入红人ID后按回车添加"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="channel" label="渠道" rules={[{ required: true, message: '请选择渠道' }]}>
            <Select placeholder="请选择渠道">
              <Option value="email">邮箱</Option>
              <Option value="wechat_work">企业微信</Option>
              <Option value="feishu">飞书</Option>
              <Option value="whatsapp">WhatsApp</Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.channel !== curr.channel}>
            {({ getFieldValue }) => {
              const channel = getFieldValue('channel') as ChannelType;
              return channel ? (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: COLORS.gray100, borderRadius: 4 }}>
                  <span style={{ color: COLORS.gray500, fontSize: 12 }}>
                    内容格式提示：{CHANNEL_CONTENT_HINTS[channel]}
                  </span>
                </div>
              ) : null;
            }}
          </Form.Item>
          <Form.Item name="tone" label="语气风格">
            <Select placeholder="请选择语气风格" allowClear>
              <Option value="professional">专业</Option>
              <Option value="friendly">友好</Option>
              <Option value="casual">随意</Option>
            </Select>
          </Form.Item>
          <Form.Item name="product_info" label="产品信息" rules={[{ required: true, message: '请输入产品信息' }]}>
            <TextArea rows={3} placeholder="请描述您的产品或服务" />
          </Form.Item>
          <Form.Item name="cooperation_info" label="合作信息" rules={[{ required: true, message: '请输入合作信息' }]}>
            <TextArea rows={3} placeholder="请描述合作意向和条件" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑消息Modal */}
      <Modal
        title={selectedMessage?.status === 'draft' ? '编辑消息' : '消息预览'}
        open={editMsgModalVisible}
        onCancel={() => {
          setEditMsgModalVisible(false);
          setSelectedMessage(null);
        }}
        onOk={() => selectedMessage?.status === 'draft' ? editMsgForm.submit() : setEditMsgModalVisible(false)}
        okText={selectedMessage?.status === 'draft' ? '保存' : '关闭'}
        cancelText="取消"
        width={700}
      >
        {selectedMessage && (
          <Form form={editMsgForm} layout="vertical" onFinish={handleUpdateMessage}>
            <Form.Item label="红人ID">
              <Text>{selectedMessage.influencer_id}</Text>
            </Form.Item>
            <Form.Item label="渠道">
              <Tag color={CHANNEL_COLORS[selectedMessage.channel] || 'default'}>
                {CHANNEL_LABELS[selectedMessage.channel] || selectedMessage.channel}
              </Tag>
            </Form.Item>
            <Form.Item name="subject" label="主题">
              <Input placeholder="请输入邮件主题" disabled={selectedMessage.status !== 'draft'} />
            </Form.Item>
            <Form.Item name="content" label="内容" rules={[{ required: true, message: '请输入消息内容' }]}>
              <TextArea
                rows={10}
                placeholder="请输入消息内容"
                disabled={selectedMessage.status !== 'draft'}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 回复详情Modal */}
      <Modal
        title="回复详情"
        open={replyDetailModalVisible}
        onCancel={() => {
          setReplyDetailModalVisible(false);
          setSelectedReply(null);
        }}
        footer={null}
        width={700}
      >
        {selectedReply && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="红人ID">{selectedReply.influencer_id}</Descriptions.Item>
            <Descriptions.Item label="渠道">{selectedReply.channel}</Descriptions.Item>
            <Descriptions.Item label="回复时间">
              {new Date(selectedReply.received_at).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="意向分类">
              {getIntentTag(selectedReply.intent_classification)}
            </Descriptions.Item>
            <Descriptions.Item label="回复内容">
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{selectedReply.reply_content}</Paragraph>
            </Descriptions.Item>
            {selectedReply.intent_analysis && (
              <Descriptions.Item label="AI分析">
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{selectedReply.intent_analysis}</Paragraph>
              </Descriptions.Item>
            )}
            {selectedReply.key_concerns && (
              <Descriptions.Item label="关键关注点">
                {JSON.stringify(selectedReply.key_concerns, null, 2)}
              </Descriptions.Item>
            )}
            {selectedReply.suggested_action && (
              <Descriptions.Item label="建议行动">
                {selectedReply.suggested_action}
              </Descriptions.Item>
            )}
            {selectedReply.follow_up_content && (
              <Descriptions.Item label="跟进内容">
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {typeof selectedReply.follow_up_content === 'string'
                    ? selectedReply.follow_up_content
                    : JSON.stringify(selectedReply.follow_up_content, null, 2)}
                </Paragraph>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 批量发送Modal */}
      <Modal
        title="批量发送"
        open={batchSendModalVisible}
        onCancel={() => {
          setBatchSendModalVisible(false);
          setSelectedRecipientIds([]);
          setScheduledSendTime(null);
        }}
        onOk={handleBatchSend}
        okText="发送"
        cancelText="取消"
      >
        <Form layout="vertical">
          <Form.Item label="收件人ID列表">
            <Select
              mode="tags"
              placeholder="输入收件人ID后按回车添加"
              style={{ width: '100%' }}
              value={selectedRecipientIds}
              onChange={setSelectedRecipientIds}
            />
          </Form.Item>
          <Form.Item label="定时发送（可选）">
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择发送时间"
              style={{ width: '100%' }}
              value={scheduledSendTime}
              onChange={(date) => setScheduledSendTime(date)}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              不选择则立即发送
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
